import axios from "axios";
import { toast } from "react-toastify";
// doLogout is loaded dynamically inside handleApiError to avoid the
// api/index.js -> actions/auth -> api/auth -> api/index.js circular dependency.
// Eager-importing it caused a TDZ crash on any first-load path that entered
// api/index.js via another api/* module (e.g. api/databaseBackups).
import i18n from '@configs/i18n';

const apiURL = process.env.REACT_APP_API_URL;
const Axios = axios.create({ baseURL: apiURL });

Axios.interceptors.request.use(
  (config) => {
    // Do something before request is sent
    const token = () => {
      if (localStorage?.getItem("accessToken"))
        return {
          headers: {
            Authorization: localStorage?.getItem("accessToken"),
          },
        };
      return {};
    };
    return {
      ...token(),
      ...config,
    };
  },
  (error) => {
    if (error.response?.status === 401) {
      /**
       * ⭐ SAY WHY, instead of a silent bounce to the login page.
       *
       * A 401 now has two meanings: the token was never valid, or it went idle past the limit
       * (App\Http\Middleware\IdleTimeout, 60 minutes - Yael's number, 2026-10-09). The server
       * tags the second with `reason: 'idle_timeout'` and a sentence; without carrying it over,
       * being signed out mid-task looks like the app losing its place.
       *
       * ⚠ sessionStorage, not a query string: the message is read once by the login page and
       * dies with the tab. A ?reason= in the URL survives bookmarking and sharing.
       */
      try {
        const reason = error.response?.data?.reason;
        const text = error.response?.data?.message;
        if (reason === "idle_timeout" && text) {
          sessionStorage.setItem("loginNotice", text);
        }
      } catch (e) { /* storage can throw in private mode - never block the redirect */ }
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Handle 403 (Forbidden) responses from Spatie permission middleware
Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      const message = error.response.data?.message || i18n.t("toast.permission_denied");
      toast(message, { type: "error", toastId: "permission-denied" });
      // Fallback: refresh permissions in case WebSocket notification was missed
      // Dynamic import to avoid circular dependency (index → permissionSync → auth → index)
      import("@src/utility/permissionSync").then(({ refreshPermissionsNow }) => {
        refreshPermissionsNow();
      });
    }
    return Promise.reject(error);
  }
);

export default Axios;

function handleApiError(e) {
  if (e.response && e.response.status === 401) {
    import("../actions/auth").then(({ doLogout }) => doLogout());
    return undefined;
  }
  if (e.response && e.response.status === 403) { return undefined; } // already handled by interceptor

  const status = e.response?.status;
  let message = i18n.t("toast.unexpected_error");

  if (status === 422) {
    const errors = e.response?.data?.errors;
    message = errors ? Object.values(errors).flat().join('\n') : (e.response?.data?.message || message);
  } else if (status === 404) {
    message = i18n.t("toast.resource_not_found");
  } else if (status === 429) {
    message = i18n.t("toast.too_many_requests");
  } else if (status >= 500) {
    message = i18n.t("toast.server_error");
  } else if (!e.response) {
    message = i18n.t("toast.network_error");
  }

  toast.error(message, { toastId: `api-error-${status || 'network'}` });
  return undefined;
}

export const headers = () => ({
  headers: { Authorization: localStorage.accessToken },
}); // with this need to send selected language as i18n.language from useTranslation

export const FileHeaders = () => ({
  headers: { Authorization: localStorage.accessToken, "Content-Type": "application/json;charset=UTF-8;multipart/form-data" },
});

export const Get = async (url, data = true) =>
  await Axios.get(url, headers())
    .then((res) => (data ? res.data.data : res.data))
    .catch((e) => {
      return handleApiError(e);
    });

export const Post = (url) => async (data) =>
  await Axios.post(url, data, headers())
    .then((res) => res.data)
    .catch((e) => {
      return handleApiError(e);
    });

export const FilePost = (url) => async (data) =>
  await Axios.post(url, data, FileHeaders())
    .then((res) => res.data)
    .catch((e) => {
      return handleApiError(e);
    });

export const Put = async (url, data = true) =>
  await Axios.put(url, data, headers())
    .then((res) => (res.data))
    .catch((e) => {
      return handleApiError(e);
    });

export const Delete = async (url, data = null) =>
  await Axios.delete(url, { ...headers(), ...(data ? { data } : {}) })
    .then((res) => res.data)
    .catch((e) => {
      return handleApiError(e);
    });
